import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Shield, Trash2, Building2, User, Users as UsersIcon, Edit2, Check, X, KeyRound, Copy } from 'lucide-react';
import { toast } from 'sonner';
import RolesManagement from '../components/RolesManagement';

const ROLE_LABELS = {
  admin: { label: 'Energy Manager', color: 'bg-purple-100 text-purple-700', desc: 'Accès total, création rapports' },
  energy_manager: { label: 'Energy Manager', color: 'bg-purple-100 text-purple-700', desc: 'Accès total, création rapports' },
  responsable_agence: { label: 'Responsable Agence', color: 'bg-blue-100 text-blue-700', desc: 'Rapports de son agence' },
  commercial: { label: 'Commercial', color: 'bg-gray-100 text-gray-700', desc: 'Ses propres rapports' }
};

const Users = () => {
  const [activeTab, setActiveTab] = useState('users'); // 'users' | 'roles'
  const [users, setUsers] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agences, setAgences] = useState([]);
  const [commerciaux, setCommerciaux] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', email: '', password: '', role: 'commercial', agence: '', commercial_id: '', custom_role_id: '' });
  const [creating, setCreating] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchUsers();
    fetchRolesAndAgences();
    fetchCommerciaux();
    fetchCustomRoles();
  }, []);

  const fetchCustomRoles = async () => {
    try {
      const { data } = await api.get('/admin/roles');
      setCustomRoles(data || []);
    } catch (error) { console.error('Erreur chargement rôles:', error); }
  };

  const fetchUsers = async () => {
    try {
      const { data } = await api.get('/users');
      setUsers(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const fetchRolesAndAgences = async () => {
    try {
      const { data } = await api.get('/users/roles');
      setAgences(data.agences || []);
    } catch (error) {
      console.error('Erreur chargement agences:', error);
    }
  };

  const fetchCommerciaux = async () => {
    try {
      const { data } = await api.get('/commerciaux');
      setCommerciaux(data || []);
    } catch (error) {
      console.error('Erreur chargement commerciaux:', error);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user.id);
    setEditForm({
      role: user.role || 'commercial',
      agence: user.agence || '',
      commercial_id: user.commercial_id || '',
      custom_role_id: user.custom_role_id || ''
    });
  };

  const handleSaveEdit = async (userId) => {
    try {
      await api.put(`/users/${userId}`, editForm);
      toast.success('Utilisateur mis à jour');
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setEditForm({});
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!createForm.name || !createForm.email || !createForm.password) {
      toast.error('Nom, email et mot de passe obligatoires');
      return;
    }
    setCreating(true);
    try {
      await api.post('/users', createForm);
      toast.success('Utilisateur créé avec succès');
      setShowCreateForm(false);
      setCreateForm({ name: '', email: '', password: '', role: 'commercial', agence: '', commercial_id: '', custom_role_id: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Supprimer cet utilisateur ?')) return;
    try {
      await api.delete(`/users/${userId}`);
      toast.success('Utilisateur supprimé');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleResetPassword = async (userId) => {
    if (!window.confirm('Réinitialiser le mot de passe de cet utilisateur ?')) return;
    try {
      const { data } = await api.post(`/users/${userId}/reset-password`);
      setResetResult(data);
      toast.success(`Mot de passe réinitialisé pour ${data.user_email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papier');
  };

  if (loading) return (
    <div className="flex items-center justify-center h-96">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#397DFF]"></div>
    </div>
  );

  if (currentUser.role !== 'admin' && currentUser.role !== 'energy_manager') {
    return (
      <div className="bg-white border border-[#BFC6CC] rounded-lg p-12 text-center">
        <Shield className="w-16 h-16 mx-auto mb-4 text-[#687175]" />
        <h2 className="text-xl font-heading font-semibold text-[#191D1E] mb-2">Accès Restreint</h2>
        <p className="text-[#687175]">Cette section est réservée aux Energy Managers (admin).</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-heading font-semibold text-[#191D1E] tracking-tight">
            Gestion des Utilisateurs
          </h1>
          <p className="text-[#687175] mt-1">Gérez les accès, rôles et permissions</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-[#BFC6CC] mb-6" data-testid="users-tabs">
        <button onClick={() => setActiveTab('users')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'users' ? 'border-[#397DFF] text-[#397DFF]' : 'border-transparent text-[#687175] hover:text-[#191D1E]'}`}
          data-testid="tab-users">
          <span className="inline-flex items-center gap-2"><UsersIcon className="w-4 h-4" /> Utilisateurs</span>
        </button>
        <button onClick={() => setActiveTab('roles')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'roles' ? 'border-[#397DFF] text-[#397DFF]' : 'border-transparent text-[#687175] hover:text-[#191D1E]'}`}
          data-testid="tab-roles">
          <span className="inline-flex items-center gap-2"><Shield className="w-4 h-4" /> Rôles & Permissions</span>
        </button>
      </div>

      {activeTab === 'roles' ? <RolesManagement /> : (
      <div>
      <div className="flex items-center justify-end mb-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-[#687175]">
            <UsersIcon className="w-4 h-4" />
            {users.length} utilisateur{users.length > 1 ? 's' : ''}
          </div>
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center gap-2 bg-[#397DFF] hover:bg-[#4672B6] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            data-testid="create-user-btn"
          >
            <User className="w-4 h-4" />
            Nouvel utilisateur
          </button>
        </div>
      </div>

      {/* Formulaire de création */}
      {showCreateForm && (
        <div className="bg-white border-2 border-[#397DFF] rounded-lg p-6 mb-6" data-testid="create-user-form">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-heading font-semibold text-[#191D1E]">Créer un utilisateur</h2>
            <button onClick={() => setShowCreateForm(false)} className="text-[#687175] hover:text-[#191D1E]">
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleCreateUser} className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#191D1E] mb-1">Nom complet *</label>
              <input
                type="text"
                required
                value={createForm.name}
                onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
                className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm focus:ring-2 focus:ring-[#397DFF]"
                placeholder="Jean Dupont"
                data-testid="create-user-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#191D1E] mb-1">Email *</label>
              <input
                type="email"
                required
                value={createForm.email}
                onChange={(e) => setCreateForm({...createForm, email: e.target.value})}
                className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm focus:ring-2 focus:ring-[#397DFF]"
                placeholder="j.dupont@placedesenergies.com"
                data-testid="create-user-email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#191D1E] mb-1">Mot de passe *</label>
              <input
                type="password"
                required
                value={createForm.password}
                onChange={(e) => setCreateForm({...createForm, password: e.target.value})}
                className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm focus:ring-2 focus:ring-[#397DFF]"
                placeholder="Minimum 6 caractères"
                data-testid="create-user-password"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#191D1E] mb-1">Rôle *</label>
              <select
                value={createForm.custom_role_id || createForm.role}
                onChange={(e) => {
                  const v = e.target.value;
                  // Si c'est un rôle par défaut (clé textuelle), on met role ; sinon custom_role_id
                  const defaultRoles = ['commercial', 'responsable_agence', 'energy_manager'];
                  if (defaultRoles.includes(v)) {
                    setCreateForm({...createForm, role: v, custom_role_id: ''});
                  } else {
                    // C'est un UUID de rôle personnalisé
                    setCreateForm({...createForm, custom_role_id: v, role: 'commercial'});
                  }
                }}
                className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm focus:ring-2 focus:ring-[#397DFF]"
                data-testid="create-user-role"
              >
                <optgroup label="Rôles par défaut">
                  <option value="commercial">Commercial</option>
                  <option value="responsable_agence">Responsable Agence</option>
                  <option value="energy_manager">Energy Manager (Admin)</option>
                </optgroup>
                {customRoles.filter(r => !r.is_system && !['default-responsable-agence', 'default-commercial'].includes(r.id)).length > 0 && (
                  <optgroup label="Rôles personnalisés">
                    {customRoles.filter(r => !r.is_system && !['default-responsable-agence', 'default-commercial'].includes(r.id)).map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#191D1E] mb-1">Agence</label>
              <select
                value={createForm.agence}
                onChange={(e) => setCreateForm({...createForm, agence: e.target.value, commercial_id: ''})}
                className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm focus:ring-2 focus:ring-[#397DFF]"
                data-testid="create-user-agence"
              >
                <option value="">Aucune</option>
                {agences.map(a => <option key={a.id || a} value={a.id || a}>{a.nom || a}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#191D1E] mb-1">Commercial lié</label>
              <select
                value={createForm.commercial_id}
                onChange={(e) => setCreateForm({...createForm, commercial_id: e.target.value})}
                className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm focus:ring-2 focus:ring-[#397DFF]"
                data-testid="create-user-commercial"
              >
                <option value="">Aucun</option>
                {commerciaux
                  .filter(c => !createForm.agence || c.agence_id === createForm.agence)
                  .map(c => <option key={c.id} value={c.id}>{c.prenom} {c.nom}</option>)
                }
              </select>
            </div>
            <div className="col-span-2 flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 border border-[#BFC6CC] text-[#687175] rounded-lg text-sm hover:bg-[#F5F6F9]"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={creating}
                className="flex items-center gap-2 bg-[#10D183] hover:bg-[#0BB873] text-white px-6 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                data-testid="create-user-submit"
              >
                {creating ? 'Création...' : 'Créer l\'utilisateur'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white border border-[#BFC6CC] rounded-lg overflow-hidden">
        <table className="w-full" data-testid="users-table">
          <thead className="bg-[#F5F6F9] border-b border-[#BFC6CC]">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-[#687175] uppercase tracking-wide">Utilisateur</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-[#687175] uppercase tracking-wide">Rôle</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-[#687175] uppercase tracking-wide">Agence</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-[#687175] uppercase tracking-wide">Commercial lié</th>
              <th className="px-6 py-4 text-right text-xs font-semibold text-[#687175] uppercase tracking-wide">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#BFC6CC]">
            {users.map((user) => {
              const isEditing = editingUser === user.id;
              const roleInfo = ROLE_LABELS[user.role] || ROLE_LABELS.commercial;
              const linkedCommercial = commerciaux.find(c => c.id === user.commercial_id);
              
              return (
                <tr key={user.id} className="hover:bg-[#F5F6F9] transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#397DFF] rounded-full flex items-center justify-center text-white font-medium">
                        {user.name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <div className="font-medium text-[#191D1E]">{user.name}</div>
                        <div className="text-sm font-mono text-[#687175]">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {isEditing ? (
                      <select
                        value={editForm.custom_role_id || editForm.role}
                        onChange={(e) => {
                          const v = e.target.value;
                          const defaultRoles = ['commercial', 'responsable_agence', 'energy_manager'];
                          if (defaultRoles.includes(v)) {
                            setEditForm({...editForm, role: v, custom_role_id: ''});
                          } else {
                            setEditForm({...editForm, custom_role_id: v});
                          }
                        }}
                        className="px-3 py-1.5 border border-[#BFC6CC] rounded text-sm focus:ring-2 focus:ring-[#397DFF]"
                        data-testid={`edit-user-role-${user.id}`}
                      >
                        <optgroup label="Rôles par défaut">
                          <option value="energy_manager">Energy Manager</option>
                          <option value="responsable_agence">Responsable Agence</option>
                          <option value="commercial">Commercial</option>
                        </optgroup>
                        {customRoles.filter(r => !r.is_system && !['default-responsable-agence', 'default-commercial'].includes(r.id)).length > 0 && (
                          <optgroup label="Rôles personnalisés">
                            {customRoles.filter(r => !r.is_system && !['default-responsable-agence', 'default-commercial'].includes(r.id)).map(r => (
                              <option key={r.id} value={r.id}>{r.name}</option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    ) : (
                      (() => {
                        const cr = customRoles.find(r => r.id === user.custom_role_id);
                        if (cr && !cr.is_system && !['default-responsable-agence', 'default-commercial'].includes(cr.id)) {
                          return <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700" title={cr.description || cr.name}>{cr.name}</span>;
                        }
                        return <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${roleInfo.color}`}>{roleInfo.label}</span>;
                      })()
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {isEditing ? (
                      <select
                        value={editForm.agence}
                        onChange={(e) => setEditForm({...editForm, agence: e.target.value})}
                        className="px-3 py-1.5 border border-[#BFC6CC] rounded text-sm focus:ring-2 focus:ring-[#397DFF]"
                      >
                        <option value="">-- Aucune --</option>
                        {agences.map(a => (
                          <option key={a} value={a}>{a}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-[#687175]">
                        {user.agence || <span className="text-[#BFC6CC]">-</span>}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {isEditing ? (
                      <select
                        value={editForm.commercial_id}
                        onChange={(e) => setEditForm({...editForm, commercial_id: e.target.value})}
                        className="px-3 py-1.5 border border-[#BFC6CC] rounded text-sm focus:ring-2 focus:ring-[#397DFF]"
                      >
                        <option value="">-- Aucun --</option>
                        {commerciaux.map(c => (
                          <option key={c.id} value={c.id}>{c.prenom} {c.nom} ({c.agence})</option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-[#687175]">
                        {linkedCommercial 
                          ? `${linkedCommercial.prenom} ${linkedCommercial.nom}`
                          : <span className="text-[#BFC6CC]">-</span>
                        }
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {isEditing ? (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleSaveEdit(user.id)}
                          className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(user)}
                          disabled={user.id === currentUser.id}
                          className="p-1.5 text-[#397DFF] hover:bg-blue-50 rounded disabled:opacity-30"
                          title="Modifier"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleResetPassword(user.id)}
                          className="p-1.5 text-[#F9C80E] hover:bg-yellow-50 rounded"
                          title="Réinitialiser le mot de passe"
                          data-testid={`reset-password-${user.id}`}
                        >
                          <KeyRound className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(user.id)}
                          disabled={user.id === currentUser.id}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded disabled:opacity-30"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modale mot de passe temporaire */}
      {resetResult && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setResetResult(null)}>
          <div className="bg-white rounded-xl border border-[#BFC6CC] p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="reset-password-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#F9C80E]/10 rounded-full flex items-center justify-center">
                <KeyRound className="w-5 h-5 text-[#F9C80E]" />
              </div>
              <h3 className="text-lg font-heading font-semibold text-[#191D1E]">Mot de passe réinitialisé</h3>
            </div>
            <p className="text-sm text-[#687175] mb-4">
              Le mot de passe temporaire pour <strong>{resetResult.user_email}</strong> est :
            </p>
            <div className="flex items-center gap-2 bg-[#F5F6F9] border border-[#BFC6CC] rounded-lg p-3 mb-4">
              <code className="flex-1 text-lg font-mono font-bold text-[#191D1E]" data-testid="temp-password-value">
                {resetResult.temporary_password}
              </code>
              <button
                onClick={() => copyToClipboard(resetResult.temporary_password)}
                className="p-2 text-[#397DFF] hover:bg-blue-50 rounded-lg transition-colors"
                title="Copier"
                data-testid="copy-temp-password"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-[#687175] mb-4">
              Communiquez ce mot de passe à l'utilisateur. Il pourra le changer après sa connexion.
            </p>
            <button
              onClick={() => setResetResult(null)}
              className="w-full bg-[#191D1E] hover:bg-[#0D3A5F] text-white py-2 rounded-lg text-sm font-medium transition-colors"
              data-testid="close-reset-modal"
            >
              Fermer
            </button>
          </div>
        </div>
      )}

      {/* Légende des rôles */}
      <div className="mt-6 bg-[#F5F6F9] border border-[#BFC6CC] rounded-lg p-6">
        <h3 className="font-heading font-semibold text-[#191D1E] mb-4">Rôles et Permissions</h3>
        <div className="grid grid-cols-3 gap-4">
          {Object.entries(ROLE_LABELS).map(([key, value]) => (
            <div key={key} className="flex items-start gap-3">
              <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${value.color}`}>
                {value.label}
              </span>
              <p className="text-sm text-[#687175]">{value.desc}</p>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-[#687175]">
          Pour créer un rôle personnalisé avec des permissions spécifiques, cliquez sur l'onglet <strong>Rôles & Permissions</strong> ci-dessus.
        </p>
      </div>
      </div>
      )}
    </div>
  );
};

export default Users;
